import { InputField } from '@/components/form/InputField';
import { Form } from '@/components/primitives/Form';
import PromoCode from '@/components/PromoCode';
import { AccordionBadgeCount } from '@/components/purchase/checkout/AccordionBadgeCount';
import { toastSuccess, toastWarning } from '@/components/toast';
import { refreshCheckoutBooking } from '@/features/purchase/purchaseActions';
import {
  addPromoCodes,
  validatePromoCode,
} from '@/features/purchase/purchaseService';
import { useBookingPromoCodes } from '@/hooks/useBookingPromoCodes';
import { useCheckoutBooking } from '@/hooks/useCheckoutBooking';
import { useSearchWidget } from '@/hooks/useSearchWidget';
import { useTranslation } from '@/i18n';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import { useDispatch } from '@/store/utils';
import type { ApiErrorResponse } from '@/types/api';
import { getBookingAdmissions } from '@/utils/booking';
import { breakpoints } from '@/utils/breakpoints';
import { showErrorToast } from '@/utils/errorToast';
import { GTM_EVENT, trackCustomEvent } from '@/utils/gtm';
import {
  createPromoCodesSchema,
  type PromoCodesValues,
} from '@/utils/zodSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Accordion,
  Button,
  Divider,
  Icons,
  Typography,
} from '@packages/webshop-design-system';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMediaQuery } from 'react-responsive';

function PromoCodes() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const booking = useCheckoutBooking();
  const isLaptopOrBigger = useMediaQuery({ minWidth: breakpoints.laptop });

  const hasVouchers = !!booking?.pendingVouchers?.length;

  const { bookingPromoCodes } = useBookingPromoCodes(booking);

  const { currentSearchFormValues } = useSearchWidget();

  // BookingDTO does not return promo codes that had no effect.
  // If any promo codes added during search were dropped while
  // moving to the checkout stage, user will be displayed a warning.
  useEffect(() => {
    if (!booking) return;
    const searchCodes = currentSearchFormValues?.promoCodes ?? [];
    const hasMissing = searchCodes.some(
      (sc) => !bookingPromoCodes.some((bc) => bc.code === sc.code)
    );
    if (hasMissing) {
      toastWarning(<TransAlert i18nKey="promoCodesDuringSearchHadNoEffect" />);
    }
  }, []);

  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(
    !hasVouchers && bookingPromoCodes.length > 0
  );

  // Current DB schema does not support adding promo codes after vouchers.
  // If user adds a voucher, promo code section will be collapsed and disabled.
  useEffect(() => {
    if (hasVouchers) {
      setIsOpen(false);
    }
  }, [hasVouchers]);

  const form = useForm<PromoCodesValues>({
    resolver: zodResolver(createPromoCodesSchema(t)),
    defaultValues: { promoCode: '' },
  });

  const onSubmit = async (values: PromoCodesValues) => {
    if (!booking?.id) return;
    setLoading(true);
    const { promoCode } = values;

    if (bookingPromoCodes.some((c) => c.code === promoCode)) {
      form.setError('promoCode', {
        message: t(
          'text.promoCodeAlreadyApplied',
          'Promo code already applied'
        ),
      });
      setLoading(false);
      return;
    }

    try {
      await validatePromoCode(promoCode);
      const updated = [
        ...bookingPromoCodes.map(({ code }) => ({ code: code! })),
        { code: promoCode },
      ];
      const { warnings } = await addPromoCodes(booking.id, updated);
      const refreshed = await dispatch(refreshCheckoutBooking()).unwrap();

      const isApplied = getBookingAdmissions(refreshed).some((admission) =>
        admission.appliedPromotionCodes?.some(({ code }) => code === promoCode)
      );
      if (!isApplied) {
        const detail = warnings?.warnings?.[0]?.detail ?? '';
        toastWarning(
          <TransAlert i18nKey="promoCodeNotApplied" values={{ detail }} />
        );
        return;
      }
      toastSuccess(<TransAlert i18nKey="promoCodeApplied" />);
      trackCustomEvent(GTM_EVENT.PROMOTIONAL_CODE_ADDED, {
        promotional_code: promoCode,
      });
      form.reset();
    } catch (e) {
      showErrorToast(
        <TransText i18nKey="promoCodeNotApplied" />,
        e as ApiErrorResponse
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      className="flex h-full w-full flex-col gap-2 pt-2"
    >
      <Accordion
        data-testid="collapse-promo-code-section-button"
        isExpanded={isOpen}
        onExpandedChange={setIsOpen}
        isDisabled={hasVouchers}
        className="border-none"
        classNames={{
          trigger: 'justify-between gap-1 text-dark py-0 hover:no-underline',
        }}
        trigger={
          <Typography
            variant="body1-bold"
            className={`relative mb-2 w-fit ${hasVouchers ? 'text-neutral' : 'text-dark'}`}
          >
            <TransText i18nKey="addPromoCode" />:
            {!!bookingPromoCodes.length && (
              <AccordionBadgeCount
                count={bookingPromoCodes.length}
                label={<TransText i18nKey="appliedPromoCodes" />}
              />
            )}
          </Typography>
        }
      >
        <div className="flex flex-col items-start gap-6 self-stretch laptop:flex-row">
          <div className="flex w-full flex-1 flex-col gap-1.5 tablet:flex-row laptop:flex-col laptop:gap-6">
            <InputField
              label=""
              name="promoCode"
              hideLabel
              required
              classNames={{ wrapper: 'flex-1' }}
            />
            <Button
              variant="secondary"
              className="flex w-full gap-1.5 tablet:max-w-24 laptop:w-fit"
              data-testid="add-promo-code-button"
              isPending={loading}
              type="submit"
              startAdornment={
                <Icons.check className="h-5 w-5 laptop:h-4 laptop:w-4" />
              }
            >
              <TransText i18nKey="apply" />
            </Button>
          </div>
          <Divider
            orientation={isLaptopOrBigger ? 'vertical' : 'horizontal'}
            grow={false}
            className="my-2 h-auto self-stretch laptop:my-0"
          />
          <div className="flex w-full flex-1 flex-col gap-2 py-2">
            <Typography variant="body1-bold" className="text-dark">
              <TransText i18nKey="appliedPromoCodes" />:
            </Typography>
            <div className="flex w-full flex-1 flex-col items-start gap-2 overflow-hidden py-2">
              {bookingPromoCodes.length ? (
                <>
                  <Typography variant="body2" className="mb-1 text-neutral">
                    <TransText i18nKey="promoCodeDisclaimer" />
                  </Typography>
                  {bookingPromoCodes.map(({ code, description }) => (
                    <PromoCode
                      key={code}
                      code={code}
                      description={description}
                      className="self-stretch tablet:mr-24 laptop:mr-0"
                    />
                  ))}
                </>
              ) : (
                <Typography variant="body2" className="text-neutral">
                  <TransText i18nKey="noPromoCodesApplied" />
                </Typography>
              )}
            </div>
          </div>
        </div>
      </Accordion>
      {hasVouchers && (
        <Typography variant="body2" className="text-neutral">
          <TransText i18nKey="cannotAddPromoCodeAfterVoucher" />
        </Typography>
      )}
    </Form>
  );
}

export default PromoCodes;
